// Org-wide AdminRetention page (item 40 of PRODUCT-ROADMAP.md,
// "AdminRetention-Page nachreichen"). Closes the bug-fix gap where
// the AdminShell sidebar's "Retention" link rendered an empty page.
//
// Layout — top to bottom:
//   * Sweeper-health banner — green when every enabled policy ran in
//     the last 2× SweepInterval window; amber when any policy is
//     stale or has never run; red when no enabled policies exist.
//   * Policies table — every retention_policies row across every repo
//     with rule summary, dry-run flag, last_run_at, and a "Preview"
//     action that opens a side-panel with match-count + sample paths.
//   * Recent deletions audit table — last 500 retention_audit rows
//     joined with repo + policy.
//
// The actual create / update / delete CRUD remains on the per-repo
// RetentionCard inside RepoDetail. This page is intentionally
// read-only: org-wide editing would compete with the project-owner
// RBAC story already in place. From here the operator clicks "Open
// repo" to land on the repo-page where the maintainer-gated card
// handles writes.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Timer,
  AlertTriangle,
  ArrowRight,
  Eye,
  CheckCircle2,
  Clock,
  X,
} from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import {
  useOrgRetentionPolicies,
  useOrgRetentionAudit,
  useRetentionSweeperHealth,
  previewRetention,
  type RetentionPolicyOrg,
  type RetentionPreview,
} from '../../api/retention';
import type { RetentionRule } from '../../api/client';

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

function ruleSummary(rule: RetentionRule | undefined): string {
  if (!rule) return '—';
  const parts: string[] = [];
  if (rule.max_age_days != null) parts.push(`max_age_days=${rule.max_age_days}`);
  if (rule.keep_per_coord != null) parts.push(`keep_per_coord=${rule.keep_per_coord}`);
  return parts.length === 0 ? '—' : parts.join(' | ');
}

function HealthBanner() {
  const { data, error } = useRetentionSweeperHealth();
  if (error) {
    return (
      <Card className="mb-4 border-danger/40 bg-danger/5">
        <CardBody>
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-danger shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-fg">
                Could not load sweeper health
              </p>
              <p className="text-sm text-fg-muted mt-1">{String(error)}</p>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }
  if (!data) return null;

  const intervalH = Math.round((data.interval_seconds / 3600) * 10) / 10;
  const stale = data.stale_enabled + data.never_run;
  const ok = data.enabled_policies > 0 && stale === 0;
  const empty = data.enabled_policies === 0;

  let tone: 'success' | 'warning' | 'danger' = 'success';
  let Icon = CheckCircle2;
  let title = 'Sweeper healthy';
  let message = '';

  if (empty) {
    tone = 'warning';
    Icon = Clock;
    title = 'No enabled retention policies';
    message =
      'The sweeper is running but has no policies to apply. Project maintainers can add policies on each repo page.';
  } else if (ok) {
    message = `${data.enabled_policies} enabled (${data.dry_run_policies} dry-run). Sweep interval ${intervalH}h. Newest run ${fmtDate(data.newest_enabled_run_at)}.`;
  } else {
    tone = 'warning';
    Icon = AlertTriangle;
    title = `${stale} policy${stale === 1 ? '' : ' policies'} stale`;
    const fragments: string[] = [];
    if (data.never_run > 0)
      fragments.push(`${data.never_run} never ran`);
    if (data.stale_enabled > 0)
      fragments.push(
        `${data.stale_enabled} last ran more than ${intervalH * 2}h ago (interval is ${intervalH}h)`,
      );
    message = `${fragments.join('; ')}. Check sweeper goroutine logs.`;
  }

  const borderClass = {
    success: 'border-success/40 bg-success/5',
    warning: 'border-warning/40 bg-warning/5',
    danger: 'border-danger/40 bg-danger/5',
  }[tone];
  const iconClass = {
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
  }[tone];

  return (
    <Card className={`mb-4 ${borderClass}`}>
      <CardBody>
        <div className="flex items-start gap-2">
          <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${iconClass}`} />
          <div className="flex-1">
            <p className="text-sm font-medium text-fg">{title}</p>
            <p className="text-sm text-fg-muted mt-1">{message}</p>
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-fg-muted">
              <span>
                Last 24h: <span className="text-fg">{data.deleted_last_24h}</span> deleted{' '}
                <span className="text-fg-subtle">· {data.dry_run_last_24h} dry-run</span>
              </span>
              {data.oldest_enabled_run_at && (
                <span>
                  Oldest run: <span className="text-fg">{fmtDate(data.oldest_enabled_run_at)}</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function previewMatchCountText(data: RetentionPreview): string {
  if (data.match_count === 0) return "No artifacts would be deleted under today's set.";
  const suffix = data.match_count === 1 ? '' : 's';
  if (data.dry_run) return `Sweeper would audit-log ${data.match_count} row${suffix} (dry-run on).`;
  return `Sweeper would delete ${data.match_count} artifact${suffix}.`;
}

function PreviewPanel({
  policy,
  onClose,
}: Readonly<{
  policy: RetentionPolicyOrg;
  onClose: () => void;
}>) {
  const [data, setData] = useState<RetentionPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    setData(null);
    previewRetention(policy.id)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [policy.id]);

  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-xl h-full bg-bg-elevated border-l border-border shadow-xl overflow-y-auto">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <p className="text-sm font-medium text-fg">Dry-run preview</p>
            <p className="text-xs text-fg-muted">
              {policy.repo_slug} · {policy.name}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="close">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="p-5 space-y-4">
          {loading && (
            <p className="text-sm text-fg-muted">Computing match set…</p>
          )}
          {err && (
            <p className="text-sm text-danger">Preview failed: {err}</p>
          )}
          {data && (
            <>
              <div>
                <p className="text-xs text-fg-muted">Rule</p>
                <p className="font-mono text-xs text-fg break-all">
                  {ruleSummary(data.rule)}
                </p>
              </div>
              <div>
                <p className="text-xs text-fg-muted">Match count</p>
                <p className="text-2xl font-medium text-fg">
                  {data.match_count}
                </p>
                <p className="text-xs text-fg-subtle">
                  {previewMatchCountText(data)}
                </p>
              </div>
              {data.samples.length > 0 && (
                <div>
                  <p className="text-xs text-fg-muted mb-1">
                    Sample paths ({data.samples.length} of {data.match_count})
                  </p>
                  <div className="max-h-80 overflow-y-auto border border-border rounded-md bg-bg-inset">
                    <ul className="text-xs font-mono divide-y divide-border">
                      {data.samples.map((s) => (
                        <li key={s.artifact_id} className="px-3 py-1.5 break-all">
                          {s.path}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function AdminRetention() {
  const policies = useOrgRetentionPolicies();
  const audit = useOrgRetentionAudit(500);
  const [previewing, setPreviewing] = useState<RetentionPolicyOrg | null>(null);

  return (
    <AdminShell
      title={
        <span className="flex items-center gap-2">
          <Timer className="h-5 w-5 text-nebula" />
          Retention
        </span>
      }
      description="Org-wide view of every property-based retention policy, the last 500 sweeper deletions, and a dry-run preview that shows exactly which artifacts a policy would touch against today's set. Per-repo create / edit / delete remains on each repo's page so the project-owner RBAC story stays intact."
    >
      <HealthBanner />

      <Card className="mb-6">
        <CardHeader title={`Policies (${policies.data?.length ?? 0})`} />
        <CardBody>
          {policies.error && (
            <p className="text-sm text-danger">
              Failed to load policies: {String(policies.error)}
            </p>
          )}
          {policies.isLoading && <p className="text-sm text-fg-muted">Loading…</p>}
          {!policies.isLoading && (!policies.data || policies.data.length === 0) && (
            <p className="text-sm text-fg-muted">
              No retention policies in this deployment yet. Project maintainers
              can add policies on the per-repo page; this view aggregates them
              once they exist.
            </p>
          )}
          {!policies.isLoading && policies.data && policies.data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-fg-muted border-b border-border">
                    <th className="py-2 pr-4">Repo</th>
                    <th className="py-2 pr-4">Format</th>
                    <th className="py-2 pr-4">Policy</th>
                    <th className="py-2 pr-4">Rule</th>
                    <th className="py-2 pr-4">State</th>
                    <th className="py-2 pr-4">Last run</th>
                    <th className="py-2 pr-4" />
                  </tr>
                </thead>
                <tbody>
                  {policies.data.map((p) => (
                    <tr key={p.id} className="border-b border-border/50">
                      <td className="py-2 pr-4">
                        <Link
                          to={`/repos/${p.repo_id}`}
                          className="text-accent hover:underline"
                        >
                          {p.repo_slug}
                        </Link>
                        <div className="text-xs text-fg-subtle">{p.repo_name}</div>
                      </td>
                      <td className="py-2 pr-4 text-xs">
                        <Badge tone="neutral">{p.repo_format}</Badge>
                      </td>
                      <td className="py-2 pr-4 font-medium">{p.name}</td>
                      <td className="py-2 pr-4 font-mono text-xs">
                        {ruleSummary(p.rule)}
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex flex-wrap gap-1">
                          <Badge tone={p.enabled ? 'success' : 'neutral'}>
                            {p.enabled ? 'enabled' : 'disabled'}
                          </Badge>
                          {p.dry_run && <Badge tone="warning">dry-run</Badge>}
                        </div>
                      </td>
                      <td className="py-2 pr-4 text-xs text-fg-muted">
                        {fmtDate(p.last_run_at)}
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setPreviewing(p)}
                            aria-label="preview"
                            title="Dry-run preview"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Link
                            to={`/repos/${p.repo_id}`}
                            className="inline-flex items-center justify-center h-8 px-3 rounded-md bg-transparent text-fg hover:bg-bg-hover transition-colors"
                            aria-label="open repo"
                            title="Open repo"
                          >
                            <ArrowRight className="h-3.5 w-3.5" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={`Recent deletions (${audit.data?.length ?? 0})`}
        />
        <CardBody>
          {audit.error && (
            <p className="text-sm text-danger">
              Failed to load audit log: {String(audit.error)}
            </p>
          )}
          {audit.isLoading && <p className="text-sm text-fg-muted">Loading…</p>}
          {!audit.isLoading && (!audit.data || audit.data.length === 0) && (
            <p className="text-sm text-fg-muted">
              No retention sweeper deletions recorded yet.
            </p>
          )}
          {!audit.isLoading && audit.data && audit.data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-fg-muted border-b border-border">
                    <th className="py-2 pr-4">When</th>
                    <th className="py-2 pr-4">Repo</th>
                    <th className="py-2 pr-4">Policy</th>
                    <th className="py-2 pr-4">Path</th>
                    <th className="py-2 pr-4">Mode</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.data.map((a, i) => (
                    <tr
                      key={`${a.policy_id}-${a.deleted_at}-${i}`}
                      className="border-b border-border/50"
                    >
                      <td className="py-2 pr-4 text-xs text-fg-muted whitespace-nowrap">
                        {fmtDate(a.deleted_at)}
                      </td>
                      <td className="py-2 pr-4">
                        <Link
                          to={`/repos/${a.repo_id}`}
                          className="text-accent hover:underline text-xs"
                        >
                          {a.repo_slug}
                        </Link>
                      </td>
                      <td className="py-2 pr-4 text-xs">{a.policy_name}</td>
                      <td className="py-2 pr-4 font-mono text-xs break-all">
                        {a.artifact_path}
                      </td>
                      <td className="py-2 pr-4">
                        {a.dry_run ? (
                          <Badge tone="warning">dry-run</Badge>
                        ) : (
                          <Badge tone="danger">deleted</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {previewing && (
        <PreviewPanel
          policy={previewing}
          onClose={() => setPreviewing(null)}
        />
      )}
    </AdminShell>
  );
}
